// =============================================================================
// Email Notification Service - Multi-Provider Support
// =============================================================================

/**
 * Email Service with support for multiple providers:
 * - SendGrid (recommended)
 * - Mailgun
 * - AWS SES
 * - Resend
 * - Local SMTP (fallback)
 */
class EmailService {
  constructor() {
    this.provider = this.determineEmailProvider();
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
    
    // Provider configurations
    this.providers = {
      SENDGRID: {
        apiKey: process.env.REACT_APP_SENDGRID_API_KEY,
        fromEmail: process.env.REACT_APP_FROM_EMAIL || 'noreply@buildzim.co.zw',
        fromName: process.env.REACT_APP_FROM_NAME || 'Eqaya Platform'
      },
      MAILGUN: {
        apiKey: process.env.REACT_APP_MAILGUN_API_KEY,
        domain: process.env.REACT_APP_MAILGUN_DOMAIN,
        fromEmail: process.env.REACT_APP_FROM_EMAIL || 'noreply@buildzim.co.zw'
      },
      AWS_SES: {
        region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
        fromEmail: process.env.REACT_APP_FROM_EMAIL || 'noreply@buildzim.co.zw'
      },
      RESEND: {
        apiKey: process.env.REACT_APP_RESEND_API_KEY,
        fromEmail: process.env.REACT_APP_FROM_EMAIL || 'noreply@buildzim.co.zw'
      },
      SMTP: {
        host: process.env.REACT_APP_SMTP_HOST || 'localhost',
        port: process.env.REACT_APP_SMTP_PORT || 587,
        user: process.env.REACT_APP_SMTP_USER,
        pass: process.env.REACT_APP_SMTP_PASS,
        fromEmail: process.env.REACT_APP_FROM_EMAIL || 'noreply@buildzim.co.zw'
      }
    };
  }

  /**
   * Determine which email provider to use based on available configuration
   */
  determineEmailProvider() {
    if (process.env.REACT_APP_SENDGRID_API_KEY) return 'SENDGRID';
    if (process.env.REACT_APP_RESEND_API_KEY) return 'RESEND';
    if (process.env.REACT_APP_MAILGUN_API_KEY && process.env.REACT_APP_MAILGUN_DOMAIN) return 'MAILGUN';
    if (process.env.REACT_APP_AWS_ACCESS_KEY_ID) return 'AWS_SES';
    if (process.env.REACT_APP_SMTP_HOST) return 'SMTP';
    return 'MOCK'; // Development fallback
  }

  /**
   * Send email notification
   */
  async sendEmail(emailData) {
    const {
      to,
      subject,
      template,
      data = {},
      priority = 'normal',
      category = 'general'
    } = emailData;

    try {
      const payload = {
        provider: this.provider,
        to: Array.isArray(to) ? to : [to],
        subject,
        template,
        data,
        priority,
        category,
        from: this.providers[this.provider]?.fromEmail,
        fromName: this.providers[this.provider]?.fromName
      };

      const response = await fetch(`${this.baseUrl}/emails/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Email send failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Email sent successfully:', result);
      return result;

    } catch (error) {
      console.error('Email send error:', error);
      throw error;
    }
  }

  /**
   * Send welcome email after registration
   */
  async sendWelcomeEmail(user) {
    return this.sendEmail({
      to: user.email,
      subject: `Welcome to Eqaya Platform, ${user.firstName}!`,
      template: 'welcome',
      data: {
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        loginUrl: `${window.location.origin}/login`,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'onboarding'
    });
  }

  /**
   * Send email verification
   */
  async sendEmailVerification(user, verificationToken) {
    const verificationUrl = `${window.location.origin}/verify-email?token=${verificationToken}&email=${encodeURIComponent(user.email)}`;
    
    return this.sendEmail({
      to: user.email,
      subject: 'Verify Your Eqaya Account',
      template: 'email_verification',
      data: {
        firstName: user.firstName,
        verificationUrl,
        expiresIn: '24 hours',
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'verification',
      priority: 'high'
    });
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email, resetToken) {
    const resetUrl = `${window.location.origin}/reset-password?token=${resetToken}&email=${encodeURIComponent(email)}`;
    
    return this.sendEmail({
      to: email,
      subject: 'Reset Your Eqaya Password',
      template: 'password_reset',
      data: {
        resetUrl,
        expiresIn: '1 hour',
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'security',
      priority: 'high'
    });
  }

  /**
   * Send escrow creation notification
   */
  async sendEscrowCreated(escrowData) {
    const { clientEmail, contractorEmail, projectId, totalAmount, currency } = escrowData;
    
    // Send to client
    await this.sendEmail({
      to: clientEmail,
      subject: `Wallet Created - Project ${projectId}`,
      template: 'escrow_created_client',
      data: {
        projectId,
        totalAmount,
        currency,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'escrow'
    });

    // Send to contractor
    await this.sendEmail({
      to: contractorEmail,
      subject: `New Project Assignment - ${projectId}`,
      template: 'escrow_created_contractor',
      data: {
        projectId,
        totalAmount,
        currency,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'escrow'
    });
  }

  /**
   * Send escrow funded notification
   */
  async sendEscrowFunded(escrowData) {
    const { contractorEmail, projectId, totalAmount, currency } = escrowData;
    
    return this.sendEmail({
      to: contractorEmail,
      subject: `Project Funded - You Can Begin Work on ${projectId}`,
      template: 'escrow_funded',
      data: {
        projectId,
        totalAmount,
        currency,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'escrow',
      priority: 'high'
    });
  }

  /**
   * Send milestone submission notification
   */
  async sendMilestoneSubmitted(milestoneData) {
    const { clientEmail, projectId, milestoneNumber, description, evidenceCount } = milestoneData;
    
    return this.sendEmail({
      to: clientEmail,
      subject: `Milestone ${milestoneNumber} Submitted - ${projectId}`,
      template: 'milestone_submitted',
      data: {
        projectId,
        milestoneNumber,
        description,
        evidenceCount,
        reviewUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'milestone',
      priority: 'high'
    });
  }

  /**
   * Send milestone approved notification
   */
  async sendMilestoneApproved(milestoneData) {
    const { contractorEmail, projectId, milestoneNumber, amount, currency } = milestoneData;
    
    return this.sendEmail({
      to: contractorEmail,
      subject: `Milestone ${milestoneNumber} Approved - Payment Released`,
      template: 'milestone_approved',
      data: {
        projectId,
        milestoneNumber,
        amount,
        currency,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'milestone',
      priority: 'high'
    });
  }

  /**
   * Send dispute raised notification
   */
  async sendDisputeRaised(disputeData) {
    const { 
      clientEmail, 
      contractorEmail, 
      adminEmails, 
      projectId, 
      disputeReason, 
      description 
    } = disputeData;
    
    // Notify all parties
    const recipients = [clientEmail, contractorEmail, ...adminEmails];
    
    return this.sendEmail({
      to: recipients,
      subject: `URGENT: Dispute Raised - Project ${projectId}`,
      template: 'dispute_raised',
      data: {
        projectId,
        disputeReason,
        description,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'dispute',
      priority: 'urgent'
    });
  }

  /**
   * Send verification assignment notification
   */
  async sendVerificationAssigned(assignmentData) {
    const { verifierEmail, projectId, milestoneNumber, verificationFee, dueDate } = assignmentData;
    
    return this.sendEmail({
      to: verifierEmail,
      subject: `New Verification Assignment - ${projectId}`,
      template: 'verification_assigned',
      data: {
        projectId,
        milestoneNumber,
        verificationFee,
        dueDate: new Date(dueDate).toLocaleDateString(),
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'verification'
    });
  }

  /**
   * Send quality inspection scheduled notification
   */
  async sendInspectionScheduled(inspectionData) {
    const { 
      inspectorEmail, 
      contractorEmail, 
      clientEmail, 
      projectId, 
      inspectionType, 
      scheduledDate,
      location 
    } = inspectionData;
    
    const recipients = [inspectorEmail, contractorEmail, clientEmail];
    
    return this.sendEmail({
      to: recipients,
      subject: `Quality Inspection Scheduled - ${projectId}`,
      template: 'inspection_scheduled',
      data: {
        projectId,
        inspectionType,
        scheduledDate: new Date(scheduledDate).toLocaleString(),
        location,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'inspection'
    });
  }

  /**
   * Send payment processed notification
   */
  async sendPaymentProcessed(paymentData) {
    const { recipientEmail, amount, currency, transactionId, paymentMethod } = paymentData;
    
    return this.sendEmail({
      to: recipientEmail,
      subject: `Payment Received - ${currency} ${amount}`,
      template: 'payment_processed',
      data: {
        amount,
        currency,
        transactionId,
        paymentMethod,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'payment',
      priority: 'high'
    });
  }

  /**
   * Send reminder notifications
   */
  async sendReminder(reminderData) {
    const { recipientEmail, type, projectId, dueDate, description } = reminderData;
    
    return this.sendEmail({
      to: recipientEmail,
      subject: `Reminder: ${type} - ${projectId}`,
      template: 'reminder',
      data: {
        type,
        projectId,
        dueDate: new Date(dueDate).toLocaleDateString(),
        description,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'reminder'
    });
  }

  /**
   * Send digest/summary emails
   */
  async sendWeeklyDigest(userEmail, digestData) {
    const { 
      firstName, 
      activeProjects, 
      pendingActions, 
      completedMilestones, 
      earnings 
    } = digestData;
    
    return this.sendEmail({
      to: userEmail,
      subject: 'Your Weekly Eqaya Summary',
      template: 'weekly_digest',
      data: {
        firstName,
        activeProjects,
        pendingActions,
        completedMilestones,
        earnings,
        dashboardUrl: `${window.location.origin}/dashboard`,
        supportEmail: 'support@buildzim.co.zw'
      },
      category: 'digest'
    });
  }

  /**
   * Get email sending statistics
   */
  async getEmailStats(period = '30d') {
    try {
      const response = await fetch(`${this.baseUrl}/emails/stats?period=${period}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Email stats error:', error);
      throw error;
    }
  }

  /**
   * Update email preferences
   */
  async updateEmailPreferences(preferences) {
    try {
      const response = await fetch(`${this.baseUrl}/emails/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) {
        throw new Error('Failed to update email preferences');
      }

      return await response.json();
    } catch (error) {
      console.error('Email preferences error:', error);
      throw error;
    }
  }

  /**
   * Get current email preferences
   */
  async getEmailPreferences() {
    try {
      const response = await fetch(`${this.baseUrl}/emails/preferences`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email preferences');
      }

      return await response.json();
    } catch (error) {
      console.error('Email preferences error:', error);
      throw error;
    }
  }

  /**
   * Test email configuration
   */
  async testEmailSetup(testEmail) {
    try {
      const response = await fetch(`${this.baseUrl}/emails/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: testEmail,
          provider: this.provider 
        })
      });

      if (!response.ok) {
        throw new Error('Email test failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Email test error:', error);
      throw error;
    }
  }

  /**
   * Get provider information
   */
  getProviderInfo() {
    return {
      activeProvider: this.provider,
      isConfigured: this.provider !== 'MOCK',
      features: this.getProviderFeatures(),
      limits: this.getProviderLimits()
    };
  }

  getProviderFeatures() {
    const features = {
      SENDGRID: ['templates', 'analytics', 'deliverability', 'webhooks'],
      MAILGUN: ['templates', 'analytics', 'tracking', 'validation'],
      AWS_SES: ['high_volume', 'cost_effective', 'integration'],
      RESEND: ['modern_api', 'developer_friendly', 'fast_delivery'],
      SMTP: ['universal', 'self_hosted', 'custom_server'],
      MOCK: ['development_only']
    };
    
    return features[this.provider] || [];
  }

  getProviderLimits() {
    const limits = {
      SENDGRID: { daily: 100, monthly: 'unlimited' },
      MAILGUN: { daily: 300, monthly: 'pay-as-go' },
      AWS_SES: { daily: 200, monthly: 'pay-as-go' },
      RESEND: { daily: 100, monthly: 3000 },
      SMTP: { daily: 'server-dependent', monthly: 'server-dependent' },
      MOCK: { daily: 'unlimited', monthly: 'development-only' }
    };
    
    return limits[this.provider] || {};
  }
}

// Export singleton instance
export const emailService = new EmailService();
export default emailService;
